import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { AppShell } from "./components/AppShell";
import { Button, EmptyState, InlineError, SkeletonRows } from "./components/ui";
import { sessionQuery } from "./lib/queries";
import { LoginPage } from "./pages/LoginPage";
import { LandingPage } from "./pages/LandingPage";

const TasksPage = lazyRouteComponent(() => import("./pages/TasksPage"), "TasksPage");
const RegisterPage = lazyRouteComponent(() => import("./pages/RegisterPage"), "RegisterPage");
const TaskDetailPage = lazyRouteComponent(() => import("./pages/TaskDetailPage"), "TaskDetailPage");
const CreateImagePage = lazyRouteComponent(() => import("./pages/CreateTaskPages"), "CreateImagePage");
const CreateVideoPage = lazyRouteComponent(() => import("./pages/CreateTaskPages"), "CreateVideoPage");
const ApiKeysPage = lazyRouteComponent(() => import("./pages/ApiKeysPage"), "ApiKeysPage");
const AdminOverviewPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminOverviewPage");
const AdminUsersPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminUsersPage");
const AdminProvidersPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminProvidersPage");
const AdminModelsPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminModelsPage");
const AdminPricingPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminPricingPage");
const AdminRoutingPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminRoutingPage");
const AdminTasksPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminTasksPage");
const AdminAuditPage = lazyRouteComponent(() => import("./pages/AdminPages"), "AdminAuditPage");

interface RouterContext {
  queryClient: QueryClient;
}

function RootNotFound() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-[100dvh] place-items-center p-4">
      <EmptyState title={t("common.errors.notFoundTitle")} description={t("common.errors.notFoundDescription")} action={<a className="button button-primary" href="/">{t("common.actions.goHome")}</a>} />
    </div>
  );
}

function RootError({ reset }: { reset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-[100dvh] place-items-center p-4">
      <div className="w-full max-w-lg"><InlineError message={t("common.errors.unexpectedDescription")} action={<Button size="sm" onClick={reset}>{t("common.actions.retry")}</Button>} /></div>
    </div>
  );
}

function AuthenticatedError({ reset }: { reset: () => void }) {
  useEffect(() => {
    document.documentElement.lang = "zh-CN";
    document.title = "API Media Console";
  }, []);

  return (
    <div className="grid min-h-[100dvh] place-items-center p-4">
      <div className="w-full max-w-lg"><InlineError message="页面无法加载，请重试。" action={<Button size="sm" onClick={reset}>重试</Button>} /></div>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: RootNotFound,
  errorComponent: ({ reset }) => <RootError reset={reset} />
});

const landingRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: LandingPage });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>) => ({ redirect: typeof search.redirect === "string" ? search.redirect : undefined }),
  component: LoginPage
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  validateSearch: (search: Record<string, unknown>) => ({ redirect: typeof search.redirect === "string" ? search.redirect : undefined }),
  component: RegisterPage
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(sessionQuery());
    } catch {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  errorComponent: ({ reset }) => <AuthenticatedError reset={reset} />,
  component: AppShell
});

const tasksRoute = createRoute({ getParentRoute: () => authenticatedRoute, path: "/tasks", component: TasksPage });
const taskDetailRoute = createRoute({ getParentRoute: () => authenticatedRoute, path: "/tasks/$taskId", component: TaskDetailPage });
const createImageRoute = createRoute({ getParentRoute: () => authenticatedRoute, path: "/create/image", component: CreateImagePage });
const createVideoRoute = createRoute({ getParentRoute: () => authenticatedRoute, path: "/create/video", component: CreateVideoPage });
const apiKeysRoute = createRoute({ getParentRoute: () => authenticatedRoute, path: "/api-keys", component: ApiKeysPage });

const adminRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: "_admin",
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(sessionQuery());
    if (user.role !== "admin") throw redirect({ to: "/tasks" });
  },
  component: Outlet
});

const adminOverviewRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin", component: AdminOverviewPage });
const adminUsersRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/users", component: AdminUsersPage });
const adminProvidersRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/providers", component: AdminProvidersPage });
const adminModelsRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/models", component: AdminModelsPage });
const adminPricingRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/pricing", component: AdminPricingPage });
const adminRoutingRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/routing", component: AdminRoutingPage });
const adminTasksRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/tasks", component: AdminTasksPage });
const adminAuditRoute = createRoute({ getParentRoute: () => adminRoute, path: "/admin/audit", component: AdminAuditPage });

export const routeTree = rootRoute.addChildren([
  landingRoute,
  loginRoute,
  registerRoute,
  authenticatedRoute.addChildren([
    tasksRoute,
    taskDetailRoute,
    createImageRoute,
    createVideoRoute,
    apiKeysRoute,
    adminRoute.addChildren([adminOverviewRoute, adminUsersRoute, adminProvidersRoute, adminModelsRoute, adminPricingRoute, adminRoutingRoute, adminTasksRoute, adminAuditRoute])
  ])
]);

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined! },
  defaultPreload: "intent",
  defaultPendingComponent: () => <div className="page"><SkeletonRows rows={6} columns={4} /></div>,
  scrollRestoration: true
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
